import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import LocalBarIcon from '@mui/icons-material/LocalBar'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import PoolIcon from '@mui/icons-material/Pool'
import RoomServiceIcon from '@mui/icons-material/RoomService'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import WifiIcon from '@mui/icons-material/Wifi'
import { alpha, Box, Button, Divider, IconButton, SvgIconProps, Tooltip, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { ComponentType } from 'react'

export type ReviewAmenityKey = 'parking' | 'wifi' | 'swim' | 'gym' | 'roomService' | 'bar'

const AMENITY_ICONS: Record<ReviewAmenityKey, ComponentType<SvgIconProps>> = {
    parking: LocalParkingIcon,
    wifi: WifiIcon,
    swim: PoolIcon,
    gym: FitnessCenterIcon,
    roomService: RoomServiceIcon,
    bar: LocalBarIcon
}

export interface HotelHeaderAmenity {
    key: ReviewAmenityKey
    available: boolean
    /** Tooltip text for the icon. */
    label: string
}

const FIELD_VALUE_SX = {
    fontSize: 13,
    fontWeight: 600,
    color: 'textColors.primary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
} as const

interface StayFieldProps {
    icon: ComponentType<SvgIconProps>
    label: string
    /** Sits beside the caption — the nights chip on the check-out leg. */
    labelAdornment?: React.ReactNode
    /** Given, the whole field is the click target. Controls that handle their own clicks omit it. */
    onEdit?: () => void
    /** Shows the hover affordance without claiming the click — for cells whose control owns it. */
    interactive?: boolean
    children: React.ReactNode
}

/**
 * One cell of the stay bar: icon, caption, value. Dates and the guests control both render
 * through it so the two can never drift apart.
 */
const StayField: React.FC<StayFieldProps> = ({ icon: Icon, label, labelAdornment, onEdit, interactive, children }) => {
    const showsHover = Boolean(onEdit) || Boolean(interactive)

    return (
    <Box
        onClick={onEdit}
        role={onEdit ? 'button' : undefined}
        tabIndex={onEdit ? 0 : undefined}
        onKeyDown={
            onEdit
                ? event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onEdit()
                  }
                : undefined
        }
        sx={theme => ({
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(showsHover && {
                cursor: 'pointer',
                position: 'relative',
                // Drawn as an overlay rather than padding: padding on a flex item changes
                // the row's metrics, which made the bar shift as the pointer moved across it.
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: '-6px -8px',
                    borderRadius: 8,
                    backgroundColor: 'transparent',
                    transition: 'background-color 120ms ease',
                    pointerEvents: 'none'
                },
                '&:hover::after': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
                '& > *': { position: 'relative' }
            })
        })}
    >
        <Icon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 11, color: 'textColors.secondary', whiteSpace: 'nowrap' }}>
                    {label}
                </Typography>
                {labelAdornment}
            </Box>
            {children}
        </Box>
    </Box>
    )
}

const NightsChip: React.FC<{ label: string; sx?: SxProps<Theme> }> = ({ label, sx }) => (
    <Typography
        component="span"
        sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            bgcolor: 'neutral.100',
            color: 'textColors.primary',
            ...sx
        }}
    >
        {label}
    </Typography>
)

interface StayLegProps {
    icon: ComponentType<SvgIconProps>
    label: string
    date: string
    onEdit?: () => void
}

/** One half of the mobile stay card: icon, caption, date. */
const StayLeg: React.FC<StayLegProps> = ({ icon: Icon, label, date, onEdit }) => (
    <Box
        onClick={onEdit}
        role={onEdit ? 'button' : undefined}
        tabIndex={onEdit ? 0 : undefined}
        sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1, px: 1.5, py: 1.75, cursor: onEdit ? 'pointer' : undefined }}
    >
        <Icon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'textColors.secondary'
                }}
            >
                {label}
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'textColors.primary', whiteSpace: 'nowrap' }}>
                {date}
            </Typography>
        </Box>
    </Box>
)

export interface HotelHeaderCardProps {
    variant: 'compact' | 'wide'
    name: string
    address: string
    /** Tourism license line under the address; omitted when the hotel has none. */
    licenseText?: string
    starCount: number
    amenities: HotelHeaderAmenity[]
    checkInLabel: string
    checkInText: string
    checkOutLabel: string
    checkOutText: string
    editDatesLabel: string
    /** Opens the date picker. Omit to render the stay dates as read-only. */
    onEditDates?: () => void
    /**
     * Label for the search control. Given, it renders as a text button instead of a bare
     * icon — a page with no prices yet needs to say what the control does.
     */
    searchLabel?: string
    /** Runs the search. Without it the search control falls back to opening the dates. */
    onSearch?: () => void
    /**
     * Rooms-and-guests control shown beside the dates. Only the property (SEO) page passes
     * one — the booking flow fixes occupancy at search time, so it renders as before.
     */
    guestsField?: React.ReactNode
    guestsLabel?: string
    /** Date without the check-in/out time — the mobile card shows the day only. */
    checkInDate?: string
    checkOutDate?: string
    /** "1 night" — the pill sitting on the divider between the two legs. */
    nightsText?: string
}

// Hotel identity block: name, stars, address, available-amenity icons, check-in/out.
// Mirrors the RN HotelInfoCard; `wide` widens type for the desktop layout.
const HotelHeaderCard: React.FC<HotelHeaderCardProps> = ({
    variant,
    name,
    address,
    licenseText,
    starCount,
    amenities,
    checkInLabel,
    checkInText,
    checkOutLabel,
    checkOutText,
    editDatesLabel,
    onEditDates,
    searchLabel,
    onSearch,
    guestsField,
    guestsLabel,
    checkInDate,
    checkOutDate,
    nightsText
}) => {
    const isWide = variant === 'wide'
    const visibleAmenities = amenities.filter(item => item.available)

    // Mobile sits on a grey page, so the card keeps its own white ground; on desktop the
    // page behind is already white.
    return (
        <Box sx={{ p: 2, ...(isWide ? {} : { bgcolor: 'neutral.white' }) }}>
            {/* The hotel name is the page's subject, so it carries the h1. */}
            <Typography
                component="h1"
                sx={{ fontSize: isWide ? 22 : 18, fontWeight: 700, color: 'textColors.primary', m: 0 }}
            >
                {name}
            </Typography>
            {starCount > 0 && (
                <Box sx={{ display: 'flex', mt: 0.5 }}>
                    {Array.from({ length: starCount }, (_, index) => (
                        <StarIcon key={index} sx={{ fontSize: 16, color: 'warning.main' }} />
                    ))}
                </Box>
            )}
            <Typography sx={{ fontSize: 13, color: 'textColors.secondary', mt: 0.5 }}>{address}</Typography>
            {licenseText && (
                <Typography sx={{ fontSize: 13, color: 'textColors.secondary', mt: 0.5 }}>{licenseText}</Typography>
            )}

            {visibleAmenities.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 2 }}>
                    {visibleAmenities.map(item => {
                        const Icon = AMENITY_ICONS[item.key]
                        const iconCircle = (
                            <Box
                                key={item.key}
                                aria-label={item.label}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: 'neutral.100',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ...(isWide ? { cursor: 'help' } : {})
                                }}
                            >
                                <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
                            </Box>
                        )
                        // Hover tooltips on desktop only, as the flight cards do.
                        if (!isWide) return iconCircle
                        return (
                            <Tooltip
                                key={item.key}
                                title={
                                    <Typography fontWeight={600} color="inherit" fontSize="12px">
                                        {item.label}
                                    </Typography>
                                }
                                placement="top"
                            >
                                {iconCircle}
                            </Tooltip>
                        )
                    })}
                </Box>
            )}

            {/* Mobile keeps the stay in a card of its own, with the guests control and the
                search action on the line below. Only the property page passes guestsField,
                so the booking flow's compact header is untouched. */}
            {!isWide && guestsField ? (
                <Box sx={{ mt: 2 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'stretch',
                            bgcolor: 'neutral.100',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2
                        }}
                    >
                        <StayLeg
                            icon={LoginIcon}
                            label={checkInLabel}
                            date={checkInDate ?? checkInText}
                            onEdit={onEditDates}
                        />
                        {/* The chip sits in a column of its own rather than floating over the
                            card, so it can never land on the check-out icon. */}
                        <Box
                            sx={{
                                position: 'relative',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: nightsText ? 72 : 'auto'
                            }}
                        >
                            <Divider orientation="vertical" flexItem sx={{ position: 'absolute', left: '50%' }} />
                            {nightsText && (
                                <NightsChip
                                    label={nightsText}
                                    sx={{
                                        position: 'relative',
                                        bgcolor: 'neutral.white',
                                        border: 1,
                                        borderColor: 'divider',
                                        color: 'primary.main'
                                    }}
                                />
                            )}
                        </Box>
                        <StayLeg
                            icon={LogoutIcon}
                            label={checkOutLabel}
                            date={checkOutDate ?? checkOutText}
                            onEdit={onEditDates}
                        />
                    </Box>

                    {/* px matches the legs' inset above, so this icon lines up with theirs. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5, px: 1.5 }}>
                        <StayField icon={PeopleOutlineIcon} label={guestsLabel ?? ''} interactive>
                            <Box
                                sx={{
                                    '& > *': { width: 'auto' },
                                    '& .MuiButtonBase-root': {
                                        ...FIELD_VALUE_SX,
                                        // Same size as the dates in the card above.
                                        fontSize: 15,
                                        fontWeight: 700,
                                        p: 0,
                                        minWidth: 0,
                                        lineHeight: 1.5,
                                        backgroundColor: 'transparent',
                                        '&:hover': { backgroundColor: 'transparent' }
                                    }
                                }}
                            >
                                {guestsField}
                            </Box>
                        </StayField>
                        {onEditDates && searchLabel && (
                            <Button
                                onClick={onSearch ?? onEditDates}
                                variant="contained"
                                startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    ml: 'auto',
                                    flexShrink: 0,
                                    height: 44,
                                    px: 2,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                {searchLabel}
                            </Button>
                        )}
                    </Box>
                </Box>
            ) : (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    rowGap: 1.5,
                    columnGap: 3,
                    mt: 2,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <StayField icon={LoginIcon} label={checkInLabel} onEdit={onEditDates}>
                    <Typography sx={FIELD_VALUE_SX}>{checkInText}</Typography>
                </StayField>
                <Divider orientation="vertical" flexItem />
                <StayField
                    icon={LogoutIcon}
                    label={checkOutLabel}
                    labelAdornment={nightsText ? <NightsChip label={nightsText} /> : undefined}
                    onEdit={onEditDates}
                >
                    <Typography sx={FIELD_VALUE_SX}>{checkOutText}</Typography>
                </StayField>
                {guestsField && (
                    <>
                        {/* Only from lg, where the row fits one line — below that it would
                            wrap and leave a divider dangling at the end of the first line. */}
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ display: { xs: 'none', lg: 'block' } }}
                        />
                        <StayField icon={PeopleOutlineIcon} label={guestsLabel ?? ''} interactive>
                        {/* The control brings its own button padding and stretches to 100%.
                            Strip both so the cell sizes to its text like the dates do. */}
                        <Box
                            sx={{
                                '& > *': { width: 'auto' },
                                '& .MuiButtonBase-root': {
                                    ...FIELD_VALUE_SX,
                                    p: 0,
                                    minWidth: 0,
                                    lineHeight: 1.5,
                                    backgroundColor: 'transparent',
                                    '&:hover': { backgroundColor: 'transparent' }
                                }
                            }}
                        >
                            {guestsField}
                            </Box>
                        </StayField>
                    </>
                )}
                {onEditDates &&
                    (searchLabel ? (
                        <Button
                            onClick={onSearch ?? onEditDates}
                            variant="contained"
                            startIcon={<SearchIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                ml: 'auto',
                                flexShrink: 0,
                                height: 36,
                                px: 2,
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            {searchLabel}
                        </Button>
                    ) : (
                        <IconButton
                            onClick={onEditDates}
                            aria-label={editDatesLabel}
                            sx={{
                                ml: 1,
                                flexShrink: 0,
                                width: 36,
                                height: 36,
                                bgcolor: 'neutral.100',
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'neutral.100', opacity: 0.85 }
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    ))}
            </Box>
            )}
        </Box>
    )
}

export { HotelHeaderCard }
export default HotelHeaderCard
